import { useCallback, useState } from 'react'
import { OptionTypeBase } from 'react-select'
import { FormikHelpers } from 'formik'

import { TRANSACTION_TYPES } from 'constants/filters'
import { formatDateToShort } from 'services/helpers'
import { DropDownItem } from 'types/components'
import useTransactionStore from 'store/transaction'

type TransactionFilterValues = {
  transactionType: DropDownItem
  merchant: string
  startDateRange: Date
  endDateRange: Date
  customerTransactionsOnly?: boolean
}

const today = new Date()
const thirteenMonthsAgo = new Date()
thirteenMonthsAgo.setMonth(thirteenMonthsAgo.getMonth() - 13)
const defaultStartDate = thirteenMonthsAgo
const TransactionFilterINITVAL: TransactionFilterValues = {
  transactionType: TRANSACTION_TYPES[0],
  merchant: '',
  startDateRange: defaultStartDate,
  endDateRange: today,
  customerTransactionsOnly: false,
}

const useTransactionsFilter = ({ memberTraderId }: { memberTraderId: string }) => {
  const [startDate, setStartDate] = useState<Date | null>(defaultStartDate)
  const [endDate, setEndDate] = useState<Date | null>(today)
  const [selectedMerchant, setSelectedMerchant] = useState<OptionTypeBase | null>(null)
  const [merchantId, setMerchantId] = useState<number>()
  const [filterStartDate, setFilterStartDate] = useState<string>()
  const [filterEndDate, setFilterEndDate] = useState<string>()
  const [transactionTypeId, setTransactionTypeId] = useState<number>()

  const transactionsLoading = useTransactionStore((state) => state.transactionsLoading)
  const getTransactions = useTransactionStore((state) => state.getTransactions)
  const isFilteredTransactions = useTransactionStore((state) => state.isFilteredTransactions)
  const setFilteredTransactionsStatus = useTransactionStore((state) => state.setFilteredTransactionsStatus)

  const initialValues: TransactionFilterValues = {
    ...TransactionFilterINITVAL,
    startDateRange: startDate || defaultStartDate,
    endDateRange: endDate || today,
  }

  const handleTransactionFilter = useCallback(
    async (
      values: TransactionFilterValues,
      actions: FormikHelpers<TransactionFilterValues>
    ) => {
      setMerchantId(parseInt(selectedMerchant?.value))
      setFilterStartDate(formatDateToShort(startDate || defaultStartDate))
      setFilterEndDate(formatDateToShort(endDate || today))
      setTransactionTypeId(parseInt(values.transactionType.value))

      const queryStartDate = formatDateToShort(startDate || defaultStartDate)
      const queryEndDate = formatDateToShort(endDate || today)
      const queryTransactionTypeId =
        values.transactionType.value === '0' ? undefined : values.transactionType.value
      const merchantIdValue = values.merchant

      actions.setSubmitting(false)
      await getTransactions(
        memberTraderId,
        undefined,
        queryTransactionTypeId,
        merchantIdValue,
        queryStartDate,
        queryEndDate,
        values.customerTransactionsOnly
      )
      setFilteredTransactionsStatus(true)
    },
    [selectedMerchant, startDate, endDate, getTransactions, memberTraderId]
  )

  const handleTransactionFilterClear = useCallback(async () => {
    await getTransactions(memberTraderId, undefined, undefined, undefined, undefined, undefined, false)
    setFilteredTransactionsStatus(false)
  }, [getTransactions, memberTraderId])

  return {
    handleTransactionFilter,
    handleTransactionFilterClear,
    merchantId,
    filterStartDate,
    setFilterStartDate,
    filterEndDate,
    setFilterEndDate,
    transactionTypeId,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    selectedMerchant,
    setSelectedMerchant,
    initialValues,
    isFilteredTransactions,
    transactionsLoading,
  }
}

export default useTransactionsFilter
